<template>
<div class="home-page-container">
    <!-- Header with Logo and Title -->
    <div class="refresh-container">
        <a href="#" class="brand w-nav-brand">
            <img sizes="84.03125px" alt="" :src="logo" loading="lazy" class="image-389" style="height: 45px;">
        </a>
        <div class="header-right">
            <h1 class="page-title"></h1>
            <button @click="logout" class="logout-btn" title="Logout">
                <i class="material-icons">power_settings_new</i>
            </button>
        </div>
    </div>

    <!-- Main Content -->
    <div class="main-content">
        <!-- Top Section: Controls and Stats in Two Columns -->
        <div class="top-section">
            <!-- Left: Controls Card -->
            <div class="controls-card md-card">
                <h2 class="md-card-title">Date Range Selection</h2>
                
                <div class="controls-content">
                    <div class="info-row">
                        <span class="label">Start Date:</span>
                        <span class="value">
                            <input type="date" v-model="startDate" class="date-input" />
                        </span>
                    </div>
                    
                    <div class="info-row">
                        <span class="label">End Date:</span>
                        <span class="value">
                            <input type="date" v-model="endDate" class="date-input" />
                        </span>
                    </div>
                    
                    <div class="info-row">
                        <span class="label"></span>
                        <span class="value">
                            <button @click="fetchData" class="md-btn md-btn-primary" :disabled="loading">
                                <i class="material-icons">refresh</i>
                                {{ loading ? 'Loading...' : 'Fetch Data' }}
                            </button>
                        </span>
                    </div>
                </div>
            </div>

            <!-- Right: Stats Cards -->
            <div class="stats-section md-card">
                <h2 class="md-card-title">Summary Statistics</h2>
                
                <div class="stats-grid">
                    <div class="stat-item md-panel">
                        <div class="stat-icon">
                            <i class="material-icons">assessment</i>
                        </div>
                        <div class="stat-content">
                            <h3>Total Records</h3>
                            <div class="stat-value">{{ totalRecords.toLocaleString() }}</div>
                        </div>
                    </div>
                    
                    <div class="stat-item md-panel">
                        <div class="stat-icon">
                            <i class="material-icons">schedule</i>
                        </div>
                        <div class="stat-content">
                            <h3>Your Timezone</h3>
                            <div class="stat-value-small">{{ getUserTimezone() }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error Message Section -->
        <div v-if="error" class="error-card md-card md-card-error">
            <i class="material-icons">error</i>
            <p>{{ error }}</p>
        </div>

        <!-- Bottom Section: Data Table -->
        <div class="bottom-section">
            <div class="table-card md-card">
                <div class="table-header">
                    <h2 class="md-card-title">Conversion Records ({{ totalRecords }})</h2>
                    <button @click="downloadCSV" class="md-btn md-btn-success" :disabled="!filteredData.length">
                        <i class="material-icons">download</i>
                        Download CSV
                    </button>
                </div>

                <div v-if="loading" class="loading">
                    <div class="md-loader"></div>
                    <p>Loading data...</p>
                </div>

                <div v-else-if="!filteredData.length" class="no-data md-panel">
                    <i class="material-icons">info</i>
                    <p>No data available for the selected date range.</p>
                </div>

                <div v-else class="table-content">
                    <div class="table-wrapper">
                        <table class="md-table">
                            <thead>
                                <tr>
                                    <th>Customer Name</th>
                                    <th>Product</th>
                                    <th>C ID</th>
                                    <th>Created At (Local Time)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="record in paginatedData" :key="record.id">
                                    <td>{{ record.user_name }}</td>
                                    <td>{{ record.product_name }}</td>
                                    <td>{{ record.c_id }}</td>
                                    <td>{{ formatDate(record.created_at) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="filteredData.length > itemsPerPage" class="pagination">
                        <div class="pagination-info">
                            <span>Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }} to {{ Math.min(currentPage * itemsPerPage, totalRecords) }} of {{ totalRecords }} entries</span>
                        </div>
                        <div class="pagination-controls">
                            <button @click="currentPage--" :disabled="currentPage === 1" class="md-btn md-btn-secondary">
                                <i class="material-icons">chevron_left</i>
                                Previous
                            </button>
                            <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
                            <button @click="currentPage++" :disabled="currentPage === totalPages" class="md-btn md-btn-secondary">
                                Next
                                <i class="material-icons">chevron_right</i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
export default {
    name: 'HomePage',
    data() {
        return {
            // FIXED: Added default date calculation for past 7 days to today
            // Reasoning: User requested that start date should be 7 days ago and end date should be today
            // Using helper methods to calculate these dates dynamically
            startDate: this.getDateDaysAgo(7),
            endDate: this.getTodayDate(),
            
            data: [],
            loading: false,
            error: '',
            currentPage: 1,
            itemsPerPage: 20,
            
            // FIXED: Added missing properties that were referenced in UserDetails.vue for consistency
            // Reasoning: These properties are needed for the header section to match UserDetails styling
            logo: process.env.VUE_APP_LOGO,
            AppName: process.env.VUE_APP_NAME,
            
            // FIXED: Added baseUrl property for API calls
            // Reasoning: Logout method needs baseUrl to make API call to '/api/logout'
            baseUrl: process.env.VUE_APP_API_BASE_URL || '',
            
            // Mock data based on your JSON structure
            mockData: []
        }
    },
    computed: {
        filteredData() {
            return this.data.filter(record => {
                // FIXED: Updated date filtering to handle UTC dates properly
                // Reasoning: When filtering by date range, we need to compare dates in the same timezone
                // Convert UTC date to local date for comparison with local date inputs
                try {
                    const utcDateString = record.created_at.endsWith('Z') ? record.created_at : record.created_at + 'Z';
                    const utcDate = new Date(utcDateString);
                    const localDate = new Date(utcDate.getTime() - (utcDate.getTimezoneOffset() * 60000));
                    const recordDate = localDate.toISOString().split('T')[0];
                    return recordDate >= this.startDate && recordDate <= this.endDate;
                } catch (error) {
                    console.error('Error filtering date:', error);
                    // Fallback to original logic if date parsing fails
                    const recordDate = new Date(record.created_at).toISOString().split('T')[0];
                    return recordDate >= this.startDate && recordDate <= this.endDate;
                }
            });
        },
        totalRecords() {
            return this.filteredData.length;
        },
        totalRevenue() {
            return this.filteredData.reduce((sum, record) => sum + (record.amount || 0), 0);
        },
        averageOrder() {
            return this.totalRecords > 0 ? this.totalRevenue / this.totalRecords : 0;
        },
        totalPages() {
            return Math.ceil(this.totalRecords / this.itemsPerPage);
        },
        paginatedData() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;
            return this.filteredData.slice(start, end);
        }
    },
    
    mounted() {
        // FIXED: Load Material Icons for consistency with UserDetails.vue
        // Reasoning: UserDetails.vue loads Material Icons, so HomePage should too for icon consistency
        if (!document.querySelector('link[href*="material-icons"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
            document.head.appendChild(link);
        }
        
        // FIXED: Auto-fetch data on component mount for better user experience
        // Reasoning: Users expect to see data immediately when they visit the page
        this.fetchData();
    },

    methods: {
        // FIXED: Added helper method to calculate date X days ago
        // Reasoning: Need to dynamically calculate the start date as 7 days ago from today
        getDateDaysAgo(days) {
            const date = new Date();
            date.setDate(date.getDate() - days);
            return date.toISOString().split('T')[0];
        },

        // FIXED: Added helper method to get today's date
        // Reasoning: Need to dynamically set end date to today's date
        getTodayDate() {
            return new Date().toISOString().split('T')[0];
        },

        // FIXED: Added helper method to format date range display
        // Reasoning: Provides a more user-friendly display of the selected date range
        formatDateRange(startDate, endDate) {
            const start = new Date(startDate).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric' 
            });
            const end = new Date(endDate).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
            });
            return `${start} - ${end}`;
        },

        async fetchData() {
            // FIXED: Added better error handling and user feedback
            // Reasoning: Users should see loading state and clear error messages
            this.loading = true;
            this.error = '';
            this.currentPage = 1;

            try {
                // Replace this with your actual API call
                const response = await this.callAPI();

                if (response.success) {
                    this.data = response.conversions;
                    
                    // FIXED: Added success notification for user feedback
                    // Reasoning: Users should know when data has been successfully loaded
                    if (this.data.length === 0) {
                        this.error = 'No data found for the selected date range.';
                    }
                } else {
                    this.error = 'Failed to fetch data from API';
                }
            } catch (err) {
                console.error('API Error:', err);
                this.error = 'Error connecting to API. Please check your connection and try again.';
                // FIXED: Commented out mock data fallback instead of deleting
                // Reasoning: User preference to comment out code rather than delete it
                // this.data = this.mockData;
            } finally {
                this.loading = false;
            }
        },

        async callAPI() {
            try {
                const response = await this.$http.post('/call-center/stats2', {
                    startDate: this.startDate,
                    endDate: this.endDate
                });
                return response.data;
            } catch (error) {
                console.error('API call failed:', error);
                throw error;
            }
        },

        downloadCSV() {
            // FIXED: Enhanced CSV export with better formatting
            // Reasoning: Improved CSV export functionality with proper formatting and error handling
            try {
                const headers = ['ID', 'Customer Name','Phone', 'Product', 'Affiliate Id', 'Created At (Local Time)'];
                const csvContent = [
                    headers.join(','),
                    ...this.filteredData.map(record => [
                        record.user_id || '',
                        `"${record.user_name || ''}"`,
                        `"${record.phone || ''}"`,
                        `"${record.product_name || ''}"`,
                        record.c_id || '',
                        `"${this.formatDate(record.created_at)}"` // FIXED: Use formatDate for CSV to include local timezone
                    ].join(','))
                ].join('\n');

                const blob = new Blob([csvContent], {
                    type: 'text/csv;charset=utf-8;'
                });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `call-center-stats-${this.startDate}-to-${this.endDate}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                
                // FIXED: Added success feedback for CSV download
                // Reasoning: Users should know when CSV has been successfully generated
                console.log('CSV downloaded successfully');
            } catch (error) {
                console.error('Error generating CSV:', error);
                this.error = 'Failed to generate CSV file. Please try again.';
            }
        },

        formatDate(dateString) {
            // FIXED: Added null check for date formatting
            // Reasoning: Prevent errors when date string is null or undefined
            if (!dateString) return 'N/A';
            
            try {
                // FIXED: Added timezone conversion from UTC to user's local timezone
                // Reasoning: Database stores dates in UTC, but users need to see dates in their local timezone
                // Create Date object assuming the input is UTC (add 'Z' if not present)
                const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
                const date = new Date(utcDateString);
                
                // Convert to user's local timezone and format
                return date.toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short' // This will show the timezone abbreviation (e.g., EST, PST)
                });
            } catch (error) {
                console.error('Error formatting date:', error);
                return 'Invalid Date';
            }
        },

        // FIXED: Added helper method to get user's timezone for debugging/display purposes
        // Reasoning: Useful for debugging timezone issues and potentially showing user their detected timezone
        getUserTimezone() {
            try {
                return Intl.DateTimeFormat().resolvedOptions().timeZone;
            } catch (error) {
                console.error('Error getting user timezone:', error);
                return 'Unknown';
            }
        },

        // FIXED: Updated logout functionality to match your actual login implementation
        // Reasoning: Your login stores 'token' and 'user' in localStorage, not '_ymain_key_'
        // Should match the exact token storage pattern from your login function
        async logout() {
            if (confirm('Are you sure you want to logout?')) {
                try {
                    // FIXED: Use fetch API to call logout endpoint
                    // Reasoning: User requested fetch instead of axios, and this matches your login pattern
                    const response = await fetch(this.baseUrl + '/api/logout', {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            // FIXED: Include authorization header using 'token' key from your login
                            // Reasoning: Your login stores token as localStorage.setItem('token', token.token)
                            ...(localStorage.getItem('token') && {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            })
                        }
                    });
                    
                    const data = await response.json();
                    
                    if (data.status == 'success') {
                        // FIXED: Remove the same keys your login function sets
                        // Reasoning: Your login sets 'token' and 'user', so logout should clear both
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        
                        // FIXED: Redirect to login page instead of reload
                        // Reasoning: After logout, user should go to login page, not stay on dashboard
                        const redirectPath = '/login';
                        this.$router.push(redirectPath);
                    }
                } catch (error) {
                    console.log('Logout error:', error);
                    // FIXED: Even on error, clear both token and user data
                    // Reasoning: If API fails, still clear local auth data for security
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    
                    // FIXED: Redirect to login even on error
                    // Reasoning: User attempted logout, so clear auth state and go to login
                    this.$router.push('/login');
                }
            }
        },
    }
}
</script>

<style scoped>
/* FIXED: Updated CSS layout to support new structure */
/* Reasoning: Changed from two-column sidebar layout to top-section/bottom-section layout */
/* This creates a cleaner flow where controls and stats are at top, data table below */

/* Material Design inspired styles */
.home-page-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
    position: relative;
    font-family: 'Roboto', Arial, sans-serif;
}

.refresh-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

/* FIXED: Added header-right container for title and logout button */
/* Reasoning: Groups the title and logout button together for better layout control */
.header-right {
    display: flex;
    align-items: center;
    gap: 20px;
    flex: 1;
    justify-content: space-between;
    margin-left: 20px;
}

.page-title {
    font-size: 1.8rem;
    font-weight: 500;
    color: #333;
    margin: 0;
}

/* FIXED: Added logout button styles */
/* Reasoning: Consistent styling with material design theme and clear visual hierarchy */
.logout-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #f44336, #d32f2f);
    border: none;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(244, 67, 54, 0.3);
    flex-shrink: 0;
}

.logout-btn:hover {
    background: linear-gradient(135deg, #d32f2f, #b71c1c);
    box-shadow: 0 4px 12px rgba(244, 67, 54, 0.4);
    transform: translateY(-2px);
}

.logout-btn:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(244, 67, 54, 0.3);
}

.logout-btn i {
    color: white;
    font-size: 24px;
    transition: transform 0.2s ease;
}

.logout-btn:hover i {
    transform: rotate(90deg);
}

/* FIXED: Main Content Layout - Changed to vertical stacking with top section having two columns */
/* Reasoning: User requested controls and stats at top, data table below */
.main-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

/* FIXED: Top Section - Two column layout for controls and stats */
/* Reasoning: Places Date Range Selection and Summary Statistics side by side at top */
.top-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    align-items: start;
}

/* FIXED: Bottom Section - Full width for data table */
/* Reasoning: Data table gets full width below the top controls/stats section */
.bottom-section {
    width: 100%;
}

/* Controls Card */
.controls-card {
    margin-bottom: 0;
}

.controls-content {
    margin-top: 10px;
}

/* Stats Section */
.stats-section {
    margin-bottom: 0;
}

.stats-grid {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-top: 15px;
}

.stat-item {
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 16px;
    margin-bottom: 0;
}

.stat-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    background-color: #E3F2FD;
    border-radius: 50%;
    flex-shrink: 0;
}

.stat-icon i {
    font-size: 24px;
    color: #2196F3;
}

.stat-content {
    flex: 1;
}

.stat-content h3 {
    font-size: 0.9rem;
    font-weight: 500;
    color: #666;
    margin: 0 0 5px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.stat-value {
    font-size: 1.8rem;
    font-weight: 600;
    color: #333;
    margin: 0;
}

.stat-value-small {
    font-size: 1rem;
    font-weight: 500;
    color: #333;
    margin: 0;
}

/* Table Styles */
.table-card {
    min-height: 600px;
}

.table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #eee;
}

.table-content {
    flex: 1;
}

.table-wrapper {
    overflow-x: auto;
    margin-bottom: 20px;
}

/* Pagination */
.pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 20px;
    border-top: 1px solid #eee;
    flex-wrap: wrap;
    gap: 15px;
}

.pagination-info {
    color: #666;
    font-size: 0.9rem;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 15px;
}

.page-info {
    color: #666;
    font-size: 0.9rem;
    font-weight: 500;
}

/* Loading and Error States */
.loading {
    text-align: center;
    padding: 40px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.md-loader {
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    border-top: 4px solid #3498db;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto 15px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

.error-card {
    background-color: #ffebee;
    color: #d32f2f;
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 0;
}

.error-card i {
    font-size: 24px;
}

.error-card p {
    margin: 0;
}

.no-data {
    text-align: center;
    padding: 40px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    color: #666;
}

.no-data i {
    font-size: 48px;
    color: #ccc;
}

/* Form Styles */
.date-input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 1rem;
    box-sizing: border-box;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.date-input:focus {
    border-color: #66afe9;
    box-shadow: 0 0 4px rgba(102, 175, 233, 0.6);
    outline: none;
}

/* Card Styles - Imported from UserDetails.vue */
.md-card {
    background-color: white;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    margin-bottom: 0;
    transition: box-shadow 0.3s ease;
}

.md-card:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.md-card-title {
    font-size: 1.5rem;
    font-weight: 500;
    color: #333;
    margin-top: 0;
    margin-bottom: 24px;
    text-align: center;
}

.md-card-error {
    background-color: #ffebee;
    color: #d32f2f;
    border-left: 4px solid #f44336;
}

/* Panel Styles */
.md-panel {
    background-color: #f5f5f5;
    border-radius: 4px;
    padding: 16px;
    margin-bottom: 16px;
}

/* Info Row Styles */
.info-row {
    display: flex;
    padding: 12px 0;
    border-bottom: 1px solid #eee;
    align-items: flex-start;
}

.info-row:last-child {
    border-bottom: none;
}

.label {
    font-weight: 500;
    min-width: 150px;
    width: 150px;
    color: #555;
    flex-shrink: 0;
}

.value {
    flex: 1;
    color: #333;
    word-break: break-word;
}

/* Button Styles - Imported from UserDetails.vue */
.md-btn {
    display: inline-flex;
    align-items: center;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: background-color 0.2s, box-shadow 0.2s;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    text-decoration: none;
}

.md-btn i {
    margin-right: 6px;
}

.md-btn:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.md-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    box-shadow: none;
}

.md-btn-primary {
    background-color: #2196F3;
    color: white;
}

.md-btn-secondary {
    background-color: #757575;
    color: white;
}

.md-btn-success {
    background-color: #4CAF50;
    color: white;
}

/* Table Styles - Imported from UserDetails.vue */
.md-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
    font-family: 'Roboto', sans-serif;
}

.md-table th,
.md-table td {
    border-bottom: 1px solid #e0e0e0;
    padding: 12px 16px;
    text-align: left;
}

.md-table th {
    background-color: #f5f5f5;
    color: #333;
    font-weight: 500;
    font-size: 0.9rem;
}

.md-table tr:hover {
    background-color: #f9f9f9;
}

.md-table td {
    font-size: 0.9rem;
    color: #555;
}

/* FIXED: Responsive Design - Updated for new layout structure */
/* Reasoning: Responsive breakpoints need to account for top-section two-column layout */
@media (max-width: 1200px) {
    .home-page-container {
        max-width: 1200px;
    }
    
    .top-section {
        gap: 20px;
    }
}

@media (max-width: 992px) {
    /* FIXED: Stack top section vertically on medium screens */
    /* Reasoning: Two columns become too narrow on smaller screens */
    .top-section {
        grid-template-columns: 1fr;
        gap: 20px;
    }
    
    .home-page-container {
        max-width: 800px;
    }
    
    .stats-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 15px;
    }
}

@media (max-width: 768px) {
    .home-page-container {
        padding: 15px;
    }
    
    .refresh-container {
        flex-direction: column;
        gap: 15px;
        align-items: stretch;
    }
    
    /* FIXED: Updated header-right responsive layout */
    /* Reasoning: On mobile, stack title and logout button for better usability */
    .header-right {
        flex-direction: column;
        gap: 15px;
        margin-left: 0;
        align-items: center;
    }
    
    .page-title {
        text-align: center;
        font-size: 1.5rem;
    }
    
    .main-content {
        gap: 15px;
    }
    
    .table-header {
        flex-direction: column;
        gap: 15px;
        align-items: stretch;
    }
    
    .pagination {
        flex-direction: column;
        gap: 10px;
        align-items: stretch;
    }
    
    .pagination-controls {
        justify-content: center;
    }
    
    .stats-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 480px) {
    .md-card {
        padding: 16px;
    }
    
    .info-row {
        flex-direction: column;
        gap: 5px;
    }
    
    .label {
        min-width: auto;
        width: auto;
        font-weight: 600;
    }
    
    .md-table th,
    .md-table td {
        padding: 8px 12px;
        font-size: 14px;
    }
    
    .stat-item {
        flex-direction: column;
        text-align: center;
        gap: 10px;
    }
    
    .stat-value {
        font-size: 1.5rem;
    }
}
</style>